// src/game/block.ts
// Polyomino block + geometry (replaces the straight-bar contract). Pure; never mutates inputs.

import type { BlockId, Cell, Color, Direction, Side } from './types';
import { DELTAS } from './types';
import { SHAPES, normalizeShape, shapeLeadEdge, shapeLanes } from './shapes';
import type { ShapeId } from './shapes';

export interface Block {
  readonly id: BlockId;
  readonly color: Color;
  readonly anchor: Cell;          // bounding-box min corner
  readonly shape: readonly Cell[]; // normalized offsets (min row/col = 0)
  readonly cover?: number;
}

export interface BlockInit {
  readonly id: BlockId;
  readonly color: Color;
  readonly anchor: Cell;
  readonly shape: readonly Cell[];
  readonly cover?: number;
}

export function createBlock(init: BlockInit): Block {
  const anchor: Cell = { row: init.anchor.row, col: init.anchor.col };
  const shape = normalizeShape(init.shape);
  if (init.cover !== undefined) {
    return { id: init.id, color: init.color, anchor, shape, cover: init.cover };
  }
  return { id: init.id, color: init.color, anchor, shape };
}

export function blockFromShapeId(
  id: BlockId, color: Color, anchor: Cell, shapeId: ShapeId, cover?: number,
): Block {
  return createBlock(
    cover !== undefined
      ? { id, color, anchor, shape: SHAPES[shapeId].cells, cover }
      : { id, color, anchor, shape: SHAPES[shapeId].cells },
  );
}

export function blockCells(block: Block): readonly Cell[] {
  return block.shape.map((o) => ({ row: block.anchor.row + o.row, col: block.anchor.col + o.col }));
}

export function blockOccupies(block: Block, cell: Cell): boolean {
  const r = cell.row - block.anchor.row;
  const col = cell.col - block.anchor.col;
  for (const o of block.shape) {
    if (o.row === r && o.col === col) {
      return true;
    }
  }
  return false;
}

export function blockBounds(block: Block): { readonly min: Cell; readonly max: Cell } {
  let maxR = 0;
  let maxC = 0;
  for (const o of block.shape) {
    if (o.row > maxR) maxR = o.row;
    if (o.col > maxC) maxC = o.col;
  }
  return {
    min: { row: block.anchor.row, col: block.anchor.col },
    max: { row: block.anchor.row + maxR, col: block.anchor.col + maxC },
  };
}

const DIR_SIDE: Readonly<Record<Direction, Side>> = {
  up: 'top', down: 'bottom', left: 'left', right: 'right',
};

export function leadingCells(block: Block, dir: Direction): readonly Cell[] {
  const d = DELTAS[dir];
  const edge = shapeLeadEdge(block.shape, DIR_SIDE[dir]);
  return edge.map((o) => ({
    row: block.anchor.row + o.row + d.drow,
    col: block.anchor.col + o.col + d.dcol,
  }));
}

/** Absolute perpendicular line indices the block spans for a move in `dir`. */
export function blockLanes(block: Block, dir: Direction): readonly number[] {
  const side = DIR_SIDE[dir];
  const vertical = dir === 'up' || dir === 'down';
  const base = vertical ? block.anchor.col : block.anchor.row;
  return shapeLanes(block.shape, side).map((l) => l + base);
}

export function translateBlock(block: Block, dir: Direction, k: number): Block {
  const d = DELTAS[dir];
  const anchor: Cell = { row: block.anchor.row + d.drow * k, col: block.anchor.col + d.dcol * k };
  return block.cover !== undefined
    ? { ...block, anchor }
    : { id: block.id, color: block.color, anchor, shape: block.shape };
}

export function isCovered(block: Block): boolean {
  return block.cover !== undefined && block.cover >= 1;
}

/**
 * Reveal step (called by obstacles.ts/moves.ts, NEVER by rules.ts):
 * cover>1 ⇒ new block with cover-1; cover===1 ⇒ new block with the `cover` key OMITTED
 * (becomes normal); uncovered ⇒ returned unchanged.
 */
export function decrementCover(block: Block): Block {
  if (block.cover === undefined) {
    return block;
  }
  if (block.cover > 1) {
    return { ...block, cover: block.cover - 1 };
  }
  return { id: block.id, color: block.color, anchor: block.anchor, shape: block.shape };
}
